<template>
  <div class="interactionCardMainWrapper" @click="navigate">
    <MainContainer>
      <template #default>
        <TrackDraftCard
          class="trackCard topContent"
          :track="track"
          :band-name="other_band_name"
        />
      </template>
      <template #bot>
        <div v-if="wasShared" class="bottomContent">
          <div class="contentWrap">
            <img
              v-if="SCREEN_WIDTH >= Breakpoints.md"
              class="image"
              :src="picture"
              alt=""
            />
            <div class="block">
              <span class="title">{{ $t('common.feedback') }}</span>
              <StatusText
                class="statusText"
                :status="lastStatus"
                :date="date"
              />
              <span class="text">{{ feedback }}</span>
            </div>
          </div>
          <div class="contentWrap second">
            <img
              v-if="SCREEN_WIDTH >= Breakpoints.md"
              class="image"
              :src="picture"
              alt=""
            />
            <div class="block">
              <span class="title">{{ $t('common.share_details') }}</span>
              <StatusText
                class="statusText"
                :status="status"
                :date="dateShared"
              />
              <span class="text">{{ link }}</span>
            </div>
          </div>
        </div>
        <div v-else class="bottomContent">
          <div class="contentWrap">
            <img
              v-if="SCREEN_WIDTH >= Breakpoints.md"
              class="image"
              :src="picture"
              alt=""
            />
            <div class="block">
              <span class="title">{{ $t('common.feedback') }}</span>
              <StatusText class="statusText" :status="status" :date="date" />
              <span v-if="status === 'Ex'" class="textHolder">
                <span class="text expired">{{
                  $t(
                    'influencer.profile.slug.interaction.card.expiredMessage00',
                  )
                }}</span>
                <span class="text expired">{{ influencer.entity }}</span>
                <span class="text expired">{{
                  $t(
                    'influencer.profile.slug.interaction.card.expiredMessage01',
                  )
                }}</span>
              </span>
              <span v-else class="text">{{ feedback }}</span>
            </div>
          </div>
        </div>
      </template>
    </MainContainer>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { defineComponent } from 'vue'

import MainContainer from '~/components/shared/containers/main.vue'
import StatusText from '~/components/shared/statusText.vue'
import TrackDraftCard from '~/components/track/select/draft.vue'

import SubmissionComputedUtils from '~/mixins/submission/computedUtils'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserBandStore } from '~/stores/userBand'
import { useUserBandSetStore } from '~/stores/userBandSet'

import picture_build_helper from '~/helpers/images/picture_build'

import { Breakpoints } from '~/enums/breakpoints'

import type InfluencerInteraction from '~/types/influencerInteraction'
import type Submission from '~/types/submission'
import type Track from '~/types/track'
import type { PropType } from 'vue'

export default defineComponent({
  components: {
    TrackDraftCard,
    StatusText,
    MainContainer,
  },
  mixins: [SubmissionComputedUtils],
  props: {
    interaction: {
      type: Object as PropType<InfluencerInteraction>,
      required: true,
    },
    influencer: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { coreFetch } = useProvideCoreFetch()
    const localePath = useLocalePath()

    return {
      coreFetch,
      localePath,
    }
  },
  data() {
    return {
      track: {} as Track,
      submission: {} as Submission,
      other_band_name: '',
      Breakpoints,
    }
  },
  computed: {
    ...mapState(useMiscResizeStore, ['SCREEN_WIDTH']),
    ...mapState(useUserBandStore, ['BAND_DATA']),
    ...mapState(useUserBandSetStore, { BAND_SET: 'LIVE_BANDS' }),
    picture(): string {
      return picture_build_helper({
        target: 'profile_picture',
        size: '400_400',
        slug: this.influencer.has_profile_picture
          ? this.influencer.slug
          : undefined,
        kind: 'influencer',
      })
    },
    date(): string {
      return this.interaction.date_sent
    },
    link(): string | undefined {
      return this.interaction.answer?.link || undefined
    },
    feedback(): string | undefined {
      return this.interaction.answer?.feedback
    },
    wasShared(): boolean {
      return this.isComplexFn
    },
    dateShared(): string {
      return this.interaction.answer?.date_linked?.length
        ? this.interaction.answer?.date_linked
        : this.interaction.date_sent
    },
    status(): string {
      let str = this.submission.status

      if (this.isComplexFn) {
        if (this.hasLastStatus && this.lastStatus !== this.submission.status)
          str += '-' + this.lastStatus
      }

      return str
    },
  },
  async mounted() {
    if (this.interaction.track > 0) {
      if (this.interaction.band.slug === this.BAND_DATA?.name) {
        await this.coreFetch
          .$get(`/track/track/${this.interaction.track}/`)
          .then((resp) => {
            this.track = resp
          })
          .catch((err) => {
            console.error(err)
          })
      } else {
        await this.getTrackFromOtherBand()
        this.getNameFromOtherBand()
      }
    }

    this.submission = this.interaction

    if (typeof this.submission.status_history === 'string') {
      this.submission.status_history = JSON.parse(
        this.submission.status_history,
      )
    }
  },
  methods: {
    navigate() {
      if (this.interaction.band.slug === this.BAND_DATA?.slug) {
        this.$router.push(
          this.localePath({
            path: '/band/dashboard/',
            query: {
              inspect: `${this.interaction.id}`,
            },
          }),
        )
      } else {
        this.$router.push(this.localePath('/band/profile/select/'))
      }
    },
    async getTrackFromOtherBand() {
      await this.coreFetch
        .$get(`/track/track/bulk/?slug=${this.interaction.band.slug}`)
        .then((resp: Track[]) => {
          resp.find((t) => t.id == this.interaction.track)
          for (let i = 0; i < resp.length; i++)
            if (resp[i].id === this.interaction.track) this.track = resp[i]
        })
        .catch((err) => {
          console.error(err)
        })
    },
    getNameFromOtherBand() {
      for (let i = 0; i < this.BAND_SET.length; i++) {
        if (this.interaction.band.slug === this.BAND_SET[i].slug)
          this.other_band_name = this.BAND_SET[i].name
      }
    },
  },
})
</script>

<style scoped>
.interactionCardMainWrapper {
  position: relative;
  width: 100%;
}

.interactionCardMainWrapper:hover {
  cursor: pointer;
}

.trackCard {
  box-shadow: unset;
  padding: unset;
}

.trackCard:hover {
  box-shadow: unset;
}

.bottomContent {
  background: rgb(249, 249, 249);
  padding: 20px;
}

.block {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
}

.title {
  color: var(--color-black);

  font-size: var(--font-size-big);
  font-weight: 500;
}

.statusText {
  margin-top: 4px;
}

.text,
.textHolder {
  color: var(--color-black);
  font-size: var(--font-size-big);
  margin-top: 8px;
  overflow-wrap: break-word;
}

.expired {
  color: var(--color-darker-grey);
}

.contentWrap {
  position: relative;
  width: 100%;
  display: flex;
}

.contentWrap.second {
  margin-top: 30px;
}

.image {
  box-shadow: var(--shadow-picture);
  height: 40px;
  width: 40px;
  border-radius: 50%;
  margin-right: 16px;
}
</style>
